import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Loader2, ChevronRight } from 'lucide-react';
import { api, AED, AED_short } from '../lib/api';

/**
 * Drill-down modal opened from a vendor variance row in the BOQ reconciliation
 * table. Shows every finalised BOQ line for that (property, vendor) with the
 * invoices allocated against it, plus any un-allocated invoice portions.
 */
export default function VendorLineReconModal({ open, onClose, propertyId, vendorId, vendorName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openLine, setOpenLine] = useState(null);

  useEffect(() => {
    if (!open || !propertyId || !vendorId) return;
    setLoading(true);
    api.get(`/properties/${propertyId}/vendors/${vendorId}/line-reconciliation`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, propertyId, vendorId]);

  if (!open) return null;

  const lines = data?.lines || [];
  const unalloc = data?.unallocated_invoices || [];

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-slate-900/40 backdrop-blur-sm"
         onClick={onClose} data-testid="vendor-line-recon-modal">
      <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} onClick={(e)=>e.stopPropagation()}
                  className="glass-strong w-full max-w-4xl max-h-[90vh] overflow-y-auto p-8">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="overline">BOQ line-level reconciliation</div>
            <div className="font-serif text-2xl mt-1">{vendorName || data?.vendor_name || 'Vendor'}</div>
            <div className="text-xs text-slate-500 mt-1">
              {data ? (
                <>
                  {data.final_boq_is_final
                    ? <>Finalised BOQ · {AED_short(data.final_boq_total_aed)}</>
                    : <>Highest-total BOQ (no final selected yet) · {AED_short(data.final_boq_total_aed)}</>}
                  {' · '}{lines.length} line item{lines.length === 1 ? '' : 's'}
                </>
              ) : '—'}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/60 rounded-full"><X size={18}/></button>
        </div>

        {loading && (
          <div className="py-16 flex items-center justify-center text-slate-400 text-sm">
            <Loader2 size={18} className="animate-spin mr-2"/> Loading line reconciliation…
          </div>
        )}

        {!loading && data && lines.length === 0 && (
          <div className="py-10 text-center text-slate-500 text-sm">
            No BOQ lines found for this vendor. Upload / finalise a BOQ first to reconcile invoices at line level.
          </div>
        )}

        {!loading && lines.length > 0 && (
          <>
            <div className="mb-4 grid grid-cols-3 gap-3 text-sm">
              <SumTile label="Total allocated" value={AED_short(data.totals?.allocated_aed)} accent="text-slate-900"/>
              <SumTile label="Total un-allocated" value={AED_short(data.totals?.unallocated_aed)} accent={data.totals?.unallocated_aed > 0 ? 'text-amber-700' : 'text-slate-500'}/>
              <SumTile label="Budget total" value={AED_short(data.final_boq_total_aed)} accent="text-slate-900"/>
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 border-b border-slate-200/60">
                  <th className="py-2 text-left">Line item</th>
                  <th className="text-right">Budget</th>
                  <th className="text-right">Invoiced</th>
                  <th className="text-right">Paid</th>
                  <th className="text-right">Variance</th>
                  <th className="text-right w-6"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map(l => {
                  const isOpen = openLine === l.boq_line_id;
                  const overspend = l.variance_aed < 0;
                  return (
                    <React.Fragment key={l.boq_line_id}>
                      <tr onClick={() => setOpenLine(isOpen ? null : l.boq_line_id)}
                           data-testid={`line-recon-row-${l.boq_line_id}`}
                           className="border-b border-slate-100/70 cursor-pointer hover:bg-white/60">
                        <td className="py-3">
                          <div className="font-medium text-slate-900">{l.description || '—'}</div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {l.category}{l.quantity != null ? ` · ${l.quantity} ${l.unit || ''}` : ''}
                            {l.allocations?.length ? ` · ${l.allocations.length} invoice${l.allocations.length === 1 ? '' : 's'}` : ''}
                          </div>
                        </td>
                        <td className="text-right">{AED_short(l.budget_aed)}</td>
                        <td className="text-right">{AED_short(l.invoiced_aed)}</td>
                        <td className="text-right text-emerald-700">{AED_short(l.paid_aed)}</td>
                        <td className={`text-right font-medium ${overspend ? 'text-red-600' : 'text-slate-900'}`}>
                          {overspend
                            ? `−${AED_short(Math.abs(l.variance_aed))}`
                            : AED_short(l.variance_aed)}
                        </td>
                        <td className="text-right">
                          <ChevronRight size={14}
                                         className={`text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}/>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={6} className="bg-slate-50/50 border-b border-slate-100/70">
                            <div className="px-3 py-3">
                              {l.allocations?.length === 0 ? (
                                <div className="text-xs text-slate-500 italic">
                                  No invoices have been allocated to this line yet.
                                </div>
                              ) : (
                                <div className="space-y-1.5">
                                  {l.allocations.map((a, i) => (
                                    <div key={i} className="flex items-center justify-between text-xs px-2 py-1.5 bg-white/70 rounded-lg">
                                      <div className="flex items-center gap-2">
                                        <span className="font-mono text-slate-700">{a.invoice_number || '—'}</span>
                                        <StatusPill status={a.status}/>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <span className="text-slate-500">
                                          {a.paid_at ? `paid ${a.paid_at.slice(0,10)}` :
                                           a.submitted_at ? `submitted ${a.submitted_at.slice(0,10)}` : ''}
                                        </span>
                                        <span className="font-semibold text-slate-900">{AED(a.amount_aed)}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {unalloc.length > 0 && (
              <div className="mt-6 p-4 rounded-2xl border border-amber-200 bg-amber-50/60">
                <div className="overline text-amber-900 mb-2">Invoices without full line allocation</div>
                <div className="text-xs text-amber-900 mb-3">
                  These invoices don&apos;t have every AED tied back to a BOQ line item — assign lines from the invoice to close the loop.
                </div>
                <div className="space-y-1.5">
                  {unalloc.map(u => (
                    <div key={u.invoice_id} className="flex items-center justify-between text-xs px-3 py-2 bg-white/70 rounded-lg">
                      <div>
                        <span className="font-mono text-slate-700">{u.invoice_number || '—'}</span>
                        <span className="ml-2 text-slate-500 truncate">{u.description}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusPill status={u.status}/>
                        <span className="text-amber-800 font-semibold">{AED(u.unallocated_aed)} un-allocated</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
}

function SumTile({ label, value, accent = 'text-slate-900' }) {
  return (
    <div className="rounded-2xl bg-white/70 border border-white/70 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-serif text-xl mt-1 ${accent}`}>{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const s = (status || '').toLowerCase();
  const cls = s === 'paid' ? 'bg-emerald-100 text-emerald-800'
    : s === 'rejected' ? 'bg-red-100 text-red-700'
    : 'bg-amber-100 text-amber-800';
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ${cls}`}>
      {s.replace(/_/g, ' ') || '—'}
    </span>
  );
}
